import React, { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { ModalKey } from '@dreamai/types';
import { useOnboardingModals } from '../../providers';
import './onboarding-modal.scss';

export interface OnboardingModalProps {
  modalKey: ModalKey;
  title: string;
  imageSrc: string;
  imageAlt?: string;
  children: React.ReactNode;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void | Promise<void>;
  onSecondaryClick?: () => void | Promise<void>;
  /** If true, modal visibility is controlled externally via `show` prop */
  controlled?: boolean;
  /** External control for modal visibility (only used when controlled=true) */
  show?: boolean;
  /** External control for closing modal (only used when controlled=true) */
  onClose?: () => void;
  className?: string;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
  modalKey,
  title,
  imageSrc,
  imageAlt = 'Onboarding illustration',
  children,
  primaryButtonText = 'Got it',
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  controlled = false,
  show: externalShow,
  onClose,
  className,
}) => {
  const { hasSeenModal, markAsSeen, loading } = useOnboardingModals();
  const [primaryLoading, setPrimaryLoading] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);

  // Determine if modal should show
  const internalShow = !loading && !hasSeenModal(modalKey);
  const show = controlled ? externalShow : internalShow;

  const handleClose = async () => {
    if (!controlled) {
      await markAsSeen(modalKey);
    }
    onClose?.();
  };

  const handlePrimaryClick = async () => {
    setPrimaryLoading(true);
    try {
      await onPrimaryClick?.();
      await handleClose();
    } finally {
      setPrimaryLoading(false);
    }
  };

  const handleSecondaryClick = async () => {
    setSecondaryLoading(true);
    try {
      await onSecondaryClick?.();
      await handleClose();
    } finally {
      setSecondaryLoading(false);
    }
  };

  const isLoading = primaryLoading || secondaryLoading;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop='static'
      keyboard={false}
      className={`onboarding-modal${className ? ` ${className}` : ''}`}
    >
      <Modal.Body>
        <div className='image-container'>
          <img src={imageSrc} alt={imageAlt} className='image' />
        </div>
        <div className='content'>
          <h2 className='title'>{title}</h2>
          <div className='text'>{children}</div>
        </div>
        <div className='actions'>
          {secondaryButtonText && (
            <Button
              variant='outline-secondary'
              onClick={handleSecondaryClick}
              className='button secondary'
              disabled={isLoading}
            >
              {secondaryLoading ? (
                <Spinner animation='border' size='sm' />
              ) : (
                secondaryButtonText
              )}
            </Button>
          )}
          <Button
            variant='primary'
            onClick={handlePrimaryClick}
            className={`${secondaryButtonText ? 'has-secondary' : ''} button`}
            disabled={isLoading}
          >
            {primaryLoading ? (
              <Spinner animation='border' size='sm' />
            ) : (
              primaryButtonText
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OnboardingModal;
